import { AnnoNode, AnnoNodeTypeName, EditorialContent, EditorialType, NodeTypeName, TitleTree } from "../../domain/domain";

export function findNode(current: (TitleTree | undefined), nodeType: NodeTypeName, number: string): (TitleTree | undefined) {
    if (current === undefined) {
        return undefined;
    }
    if (current.nodeType === nodeType) {
        if (current.citationPart === number) {
            return current;
        }
    }
    let ret = undefined;
    current.children.forEach(node => {
        let result = findNode(node, nodeType, number);
        if (result) {
            ret = result;
        }
    })
    return ret;
}

export const findSubTreeByNodeId = (tree: TitleTree, nodeId: number): (TitleTree | undefined) => {
    if (tree.nodeId === nodeId) {
        return tree;
    }
    let ret = undefined;
    tree.children.forEach(c => {
        let found = findSubTreeByNodeId(c, nodeId);
        if (found) {
            ret = found;
        }
    });
    return ret;
}

export const findSubTreeByannoId = (tree: AnnoNode, nodeId: number): (AnnoNode | undefined) => {
    if (tree.annoId === undefined && nodeId === undefined) return undefined;
    if (tree.annoId === nodeId) {
        return tree;
    }
    let ret = undefined;
    tree?.children?.forEach(c => {
        let found = findSubTreeByannoId(c, nodeId);
        if (found) {
            ret = found;
        }
    });

    return ret;
}

export const expandNode = (root: TitleTree, nodeId: number): number[] => {
    let nodesToExpand = expandAll(root, nodeId);
    let ids = nodesToExpand.map(node => node.nodeId);
    return ids;
}

export function expandAll(current: TitleTree, toExpandId: number): TitleTree[] {
    if (current.nodeId === toExpandId) {
        return [current];
    } else {
        let ret: TitleTree[] = [];
        current.children.forEach(node => {
            let result = expandAll(node, toExpandId);
            result.forEach(node => {
                ret.push(node);
            })
        })
        if (ret.length > 0) {
            ret.push(current);
        }
        return ret;
    }
}

export const updateURL = (treeRoot: TitleTree, subTree: TitleTree, history: any) => {
    let current: (TitleTree | undefined) = subTree;
    while (current!.nodeType !== NodeTypeName.TITLE) {
        current = findSubTreeByNodeId(treeRoot, current!.parentId);
    }
    history.push(`/component/${current?.citation}/?node=${subTree?.nodeId}`);
}

export function getEditorialContentByType(editorialContent: EditorialContent[], type: EditorialType): (EditorialContent | undefined) {
    for (var i = 0; i < editorialContent.length; i++) {
        if (editorialContent[i].editorialType === type) {
            return editorialContent[i];
        }
    }
    return undefined;
}

export function isShowRevisions(originalText: string, savedText: string) {
    if (savedText === null || originalText === savedText) {
        return false;
    }
    return true;
}

export const ALL_NODE_TYPES = [
    NodeTypeName.ARTICLE,
    NodeTypeName.ARTICLE_PART,
    NodeTypeName.ARTICLE_SUBPART,
    NodeTypeName.DIVISION,
    NodeTypeName.DIVISION_PART,
    NodeTypeName.PARAGRAPH,
    NodeTypeName.SECTION,
    NodeTypeName.SUBPARAGRAPH,
    NodeTypeName.SUB_SUBPARAGRAPH,
    NodeTypeName.SUBSECTION,
    NodeTypeName.TITLE
];

export function getNodeTypeNameText(nodeTypeName: NodeTypeName): string {
    switch (nodeTypeName) {
        case NodeTypeName.ARTICLE:
            return "Article";
        case NodeTypeName.ARTICLE_PART:
            return "Article Part";
        case NodeTypeName.ARTICLE_SUBPART:
            return "Article Subpart";
        case NodeTypeName.DIVISION:
            return "Division";
        case NodeTypeName.DIVISION_PART:
            return "Subdivision";
        case NodeTypeName.PARAGRAPH:
            return "Paragraph";
        case NodeTypeName.SECTION:
            return "Section";
        case NodeTypeName.SUBPARAGRAPH:
            return "Subparagraph";
        case NodeTypeName.SUB_SUBPARAGRAPH:
            return "Sub-subparagraph";
        case NodeTypeName.SUBSECTION:
            return "Subsection";
        case NodeTypeName.TITLE:
            return "Title";
        default:
            return "Node";
    }
}

export function getAnnoNodeTypeNameText(annoNodeTypeName: AnnoNodeTypeName): string {
    switch (annoNodeTypeName) {
        case AnnoNodeTypeName.ANNOTATION:
            return "Annotation";
        case AnnoNodeTypeName.HEADING:
            return "Annotattion Type";
        case AnnoNodeTypeName.DIVISION:
            return "Division";
        case AnnoNodeTypeName.SUBDIVISION:
            return "Subdivision";
        case AnnoNodeTypeName.SUBSUBDIVISION:
            return "SubSubdivision";
        case AnnoNodeTypeName.CONTENT:
            return "Content";
        default:
            return "Annotation Node";
    }
}

export const reorder = <T>(
    list: T[],
    startIndex: number,
    endIndex: number
): T[] => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
};

export const isBelowSection = (nodeType: NodeTypeName | undefined) => {
    if (nodeType === undefined) {
        return false;
    }
    if ([NodeTypeName.SUBSECTION, NodeTypeName.PARAGRAPH, NodeTypeName.SUBPARAGRAPH, NodeTypeName.SUB_SUBPARAGRAPH].includes(nodeType)) {
        return true;
    }
    return false;
}

export function findCitationLabel(label: string): string | undefined {
    let idx = label.lastIndexOf('(');
    if (idx < 0) {
        return undefined;
    }
    return label.substring(idx);
}

export function getEditorialPlaceholder(editorialType: EditorialType): string | undefined {
    if (editorialType === EditorialType.SOURCE_NOTE) {
        return "<t></t><b>Source:</b>";
    } else if (editorialType === EditorialType.EDITORS_NOTE) {
        return "<t></t><b>Editor\'s note:</b>";
    } else if (editorialType === EditorialType.CROSS_REFERENCES) {
        return "<t></t><b>Cross references:</b>"
    }
    return undefined;
}

export const newAddedChildrenData: Record<string, any> = {
    heading: {
        type: "HEADING",
        ordinal: 0,
        workingCopy: "ANNOTATION TYPE",
        approvedCopy: "",
        children: [],
        subTreeHasUnapprovedChanges: false,
        nodeHasChanges: false,
    },
    division: {
        type: "DIVISION",
        ordinal: 0,
        workingCopy: "DIVISION",
        approvedCopy: "",
        children: [],
        subTreeHasUnapprovedChanges: false,
        nodeHasChanges: false,
    },
    subdivision: {
        type: "SUBDIVISION",
        ordinal: 0,
        workingCopy: "SUBDIVISION",
        approvedCopy: "",
        children: [],
        subTreeHasUnapprovedChanges: false,
        nodeHasChanges: false,
    },
    subsubdivision: {
        type: "SUBSUBDIVISION",
        ordinal: 0,
        workingCopy: "SUBSUBDIVISION",
        approvedCopy: "",
        children: [],
        subTreeHasUnapprovedChanges: false,
        nodeHasChanges: false,
    },
    annotext: {
        type: "ANNOTEXT",
        ordinal: 0,
        workingCopy: "<p>Content</p>",
        approvedCopy: "",
        children: [],
        subTreeHasUnapprovedChanges: false,
        nodeHasChanges: false,
    }
}

export const getLevelsHeirarchy = (): any => {
    return {
        annotation: ['HEADING', 'DIVISION', 'SUBDIVISION', 'SUBSUBDIVISION', 'ANNOTEXT'],
        heading: ['DIVISION', 'SUBDIVISION', 'SUBSUBDIVISION', 'ANNOTEXT'],
        division: ['SUBDIVISION', 'SUBSUBDIVISION', 'ANNOTEXT'],
        subdivision: ['SUBSUBDIVISION', 'ANNOTEXT'],
        subsubdivision: ['ANNOTEXT'],
    };
}

export const removePTags = (input: string): string => input.replace(/<\/?p>/g, '');

export function isCitationValid(citation: string, nodeType: NodeTypeName, titleCitation?: string): boolean {
    // If the node type doesn't require a citation, it's always valid
    if (doesNotRequireCitation(nodeType)) return true;

    // Special titles have different rules
    if (isSpecialTitle(titleCitation)) {
        return true;
    }

    // Use specific validation based on node type
    if (nodeType === NodeTypeName.ARTICLE) {
        return validateArticleCitation(citation);
    }

    if (nodeType === NodeTypeName.SECTION) {
        return validateSectionCitation(citation);
    }

    if (nodeType === NodeTypeName.SUBSECTION) {
        return validateSubsectionCitation(citation);
    }

    if (nodeType === NodeTypeName.PARAGRAPH) {
        return validateParagraphCitation(citation);
    }

    if (nodeType === NodeTypeName.SUBPARAGRAPH) {
        return validateSubparagraphCitation(citation);
    }

    if (nodeType === NodeTypeName.SUB_SUBPARAGRAPH) {
        return validateSubSubparagraphCitation(citation);
    }

    return validateGeneralCitation(citation, nodeType);
}

// Define basic patterns
const NUM_PART = '\\d+(\\.\\d+)?';                           // Matches integers or decimal numbers (e.g., "1" or "1.5")
const ARTICLE_PATTERN = `${NUM_PART}-${NUM_PART}`;           // Matches pattern like "1-2"
const SECTION_PATTERN = `${ARTICLE_PATTERN}-${NUM_PART}`;    // Matches pattern like "1-2-3"
const PARENTHETICAL = '\\([^()]*\\)';                        // Matches parenthetical expressions like "(a)" or "(iv)"
const alphaNumericPattern = '[a-zA-Z0-9().]+';               // Matches alphanumeric characters plus parentheses and periods

export function validateGeneralCitation(citation: string, nodeType: NodeTypeName): boolean {
    // Early return if citation is empty
    if (!citation) return false;

    // No validation needed for these node types
    const nodesWithoutCitationRequirement = [
        NodeTypeName.DIVISION,
        NodeTypeName.DIVISION_PART,
        NodeTypeName.ARTICLE_PART,
        NodeTypeName.ARTICLE_SUBPART
    ];

    if (nodesWithoutCitationRequirement.includes(nodeType)) {
        return true;
    }

    // Define which node types can only have numeric citations
    const numericOnlyNodeTypes = [
        NodeTypeName.ARTICLE,
        NodeTypeName.TITLE,
        NodeTypeName.SECTION
    ];

    // Determine the appropriate pattern based on node type
    const isNumericOnly = numericOnlyNodeTypes.includes(nodeType);
    const contentPattern = isNumericOnly ? NUM_PART : alphaNumericPattern;

    // Build the regex pattern
    const regexPattern = new RegExp(
        `^${NUM_PART}` +                      // First part (always numeric)
        `(?:-${NUM_PART})?` +                 // Optional second part (always numeric)
        `(?:-(${contentPattern}))?` +         // Optional third part (numeric or alphanumeric)
        `#?$`                                 // Optional trailing hash
    );

    return regexPattern.test(citation);
}

export function validateArticleCitation(citation: string): boolean {
    if (!citation) return false;
    // Articles must match pattern like "1-2" with optional trailing hash
    const pattern = new RegExp(`^${ARTICLE_PATTERN}#?$`);
    return pattern.test(citation);
}

export function validateSectionCitation(citation: string): boolean {
    if (!citation) return false;
    // Sections must match pattern like "1-2-3" with optional trailing hash
    const pattern = new RegExp(`^${SECTION_PATTERN}#?$`);
    return pattern.test(citation);
}

export function validateSubsectionCitation(citation: string): boolean {
    if (!citation) return false;
    // Subsections must match pattern like "1-2-3(a)" with optional trailing hash
    const pattern = new RegExp(`^${SECTION_PATTERN}${PARENTHETICAL}#?$`);
    return pattern.test(citation);
}

export function validateParagraphCitation(citation: string): boolean {
    if (!citation) return false;
    // Paragraphs must match pattern like "1-2-3(a)(1)" with optional trailing hash
    const pattern = new RegExp(`^${SECTION_PATTERN}${PARENTHETICAL}${PARENTHETICAL}#?$`);
    return pattern.test(citation);
}

export function validateSubparagraphCitation(citation: string): boolean {
    if (!citation) return false;
    // Subparagraphs must match pattern like "1-2-3(a)(1)(i)" with optional trailing hash
    const pattern = new RegExp(`^${SECTION_PATTERN}${PARENTHETICAL}${PARENTHETICAL}${PARENTHETICAL}#?$`);
    return pattern.test(citation);
}

export function validateSubSubparagraphCitation(citation: string): boolean {
    if (!citation) return false;
    // Sub-subparagraphs must match pattern like "1-2-3(a)(1)(i)(A)" with optional trailing hash
    const pattern = new RegExp(`^${SECTION_PATTERN}${PARENTHETICAL}${PARENTHETICAL}${PARENTHETICAL}${PARENTHETICAL}#?$`);
    return pattern.test(citation);
}

export function doesNotRequireCitation(nodeType: NodeTypeName): boolean {
    // Check if node belongs to types exempt from citation requirements
    return [
        NodeTypeName.DIVISION,
        NodeTypeName.DIVISION_PART,
        NodeTypeName.ARTICLE_PART,
        NodeTypeName.ARTICLE_SUBPART
    ].includes(nodeType);
}

const SPECIAL_TITLES = new Set([0, 0.5, 90]);

export function isSpecialTitle(titleCitation?: string): boolean {
    if (!titleCitation) return false;

    // Extract the title number (first part before dash)
    const titlePart = titleCitation.split('-')[0];
    const titleNumber = parseFloat(titlePart);

    // Check against our set of special titles
    return SPECIAL_TITLES.has(titleNumber);
}

export function getCitationErrorMessage(citation: string, nodeType: NodeTypeName, titleCitation?: string): string {
    // Early return cases
    if (doesNotRequireCitation(nodeType) || isSpecialTitle(titleCitation)) {
        return "";
    }

    const nodeTypeText = getNodeTypeNameText(nodeType);

    // Required citation check
    if (!citation) {
        return `${nodeTypeText} citation is required`;
    }

    // Generate error message for invalid citation format
    const formatError = getFormatErrorMessage(nodeType, nodeTypeText, citation);
    if (formatError) {
        return formatError;
    }

    return "";
}

// Helper function to check format and return appropriate error message
function getFormatErrorMessage(nodeType: NodeTypeName, nodeTypeText: string, citation: string): string {
    // Handle specific validation based on node type
    switch (nodeType) {
        case NodeTypeName.ARTICLE:
            if (!validateArticleCitation(citation)) {
                return `Invalid ${nodeTypeText} citation. Please use format: #-#`;
            }
            break;
        case NodeTypeName.SECTION:
            if (!validateSectionCitation(citation)) {
                return `Invalid ${nodeTypeText} citation. Please use format: #-#-#`;
            }
            break;
        case NodeTypeName.SUBSECTION:
            if (!validateSubsectionCitation(citation)) {
                return `Invalid ${nodeTypeText} citation. Please use format: #-#-#(x)`;
            }
            break;
        case NodeTypeName.PARAGRAPH:
            if (!validateParagraphCitation(citation)) {
                return `Invalid ${nodeTypeText} citation. Please use format: #-#-#(x)(y)`;
            }
            break;
        case NodeTypeName.SUBPARAGRAPH:
            if (!validateSubparagraphCitation(citation)) {
                return `Invalid ${nodeTypeText} citation. Please use format: #-#-#(x)(y)(z)`;
            }
            break;
        case NodeTypeName.SUB_SUBPARAGRAPH:
            if (!validateSubSubparagraphCitation(citation)) {
                return `Invalid ${nodeTypeText} citation. Please use format: #-#-#(x)(y)(z)(w)`;
            }
            break;
    }

    // General validation as a fallback
    if (!validateGeneralCitation(citation, nodeType)) {
        return `Invalid ${nodeTypeText} citation format`;
    }

    return "";
}